import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChevronDown, ChevronRight, TrendingUp, TrendingDown, DollarSign, Calendar, Target, Users, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

function flattenTree(nodes, depth, parentExpanded) {
  const result = [];
  for (const node of nodes) {
    result.push({ ...node, depth, visible: parentExpanded });
    if (node.children && node.children.length > 0) {
      const childItems = flattenTree(node.children, depth + 1, true);
      result.push(...childItems);
    }
  }
  return result;
}

export default function UserTree() {
  const { api } = useApp();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const treeRes = await api.getUserTree();
      setTree(treeRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleDragStart = (e, nodeId) => {
    setDragId(nodeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', nodeId);
  };

  const handleDragOver = (e, nodeId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (nodeId !== dragId) setDropTarget(nodeId);
  };

  const handleDragLeave = () => setDropTarget(null);

  const handleDrop = async (e, targetNodeId) => {
    e.preventDefault();
    setDropTarget(null);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (!draggedId || draggedId === targetNodeId) { setDragId(null); return; }
    try {
      await api.updateUser(draggedId, { manager_id: targetNodeId });
      toast.success('Team member reassigned');
      await load();
    } catch (err) {
      toast.error('Failed to reassign');
    }
    setDragId(null);
  };

  const handleDragEnd = () => { setDragId(null); setDropTarget(null); };

  const flatItems = flattenTree(tree, 0, true);

  const isVisible = (item, allItems) => {
    let parent = allItems.find(i => i.children?.some(c => c.id === item.id));
    while (parent) {
      if (collapsed[parent.id]) return false;
      parent = allItems.find(i => i.children?.some(c => c.id === parent.id));
    }
    return true;
  };

  const toggleNode = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading org tree...</div>
      </div>
    );
  }

  const avatarColor = {
    business_head: 'bg-amber-600 text-white',
    zonal_head: 'bg-amber-500 text-white',
    sales_manager: 'bg-blue-500 text-white',
    assistant_manager: 'bg-purple-500 text-white',
    team_leader: 'bg-emerald-500 text-white',
    sales_point_business: 'bg-purple-100 text-purple-700',
    sales_point_individual: 'bg-blue-100 text-blue-700',
  };

  return (
    <div data-testid="user-tree-page" className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Organization Tree</h1>
        <p className="text-sm text-slate-500 mt-1">Drag and drop team members to reassign reporting structure</p>
      </div>
      <div className="space-y-2">
        {flatItems.filter(item => isVisible(item, flatItems)).map(node => {
          const kpis = node.kpis || {};
          const hasKids = node.children && node.children.length > 0;
          const isCollapsed = collapsed[node.id];
          const rev = ((kpis.revenue || 0) / 100000).toFixed(1);
          const tgt = ((kpis.target || 0) / 100000).toFixed(1);
          const grow = kpis.growth || 0;
          const isDragging = dragId === node.id;
          const isOver = dropTarget === node.id;

          return (
            <div
              key={node.id}
              data-testid={`tree-node-${node.id}`}
              style={{ marginLeft: node.depth * 24 }}
              draggable
              onDragStart={(e) => handleDragStart(e, node.id)}
              onDragOver={(e) => handleDragOver(e, node.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, node.id)}
              onDragEnd={handleDragEnd}
            >
              <Card className={`border shadow-sm transition-all cursor-grab active:cursor-grabbing
                ${node.depth === 0 ? 'border-amber-200' : 'border-border/60'}
                ${isDragging ? 'opacity-40 scale-95' : 'hover:shadow-md'}
                ${isOver ? 'ring-2 ring-amber-400 border-amber-400 bg-amber-50/30' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />

                    {hasKids ? (
                      <button data-testid={`toggle-node-${node.id}`} onClick={() => toggleNode(node.id)}
                        className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-slate-100 flex-shrink-0">
                        {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                      </button>
                    ) : <div className="w-7 h-7 flex-shrink-0" />}

                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${avatarColor[node.role] || 'bg-slate-200 text-slate-700'}`}>
                      {node.name?.charAt(0)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-900 truncate">{node.name}</p>
                        <Badge variant="secondary" className="text-[10px] shrink-0">{ROLE_LABELS[node.role] || node.role}</Badge>
                        {node.zone && node.zone !== 'All' && (
                          <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{node.zone}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{node.email}</p>
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-center hidden lg:block" title="Revenue">
                        <div className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-slate-400" /><span className="text-xs font-bold text-slate-800">{rev}L</span></div>
                        <p className="text-[9px] text-slate-400 uppercase">Revenue</p>
                      </div>
                      <div className="text-center hidden lg:block" title="Target">
                        <div className="flex items-center gap-1"><Target className="w-3 h-3 text-slate-400" /><span className="text-xs font-bold text-slate-800">{tgt}L</span></div>
                        <p className="text-[9px] text-slate-400 uppercase">Target</p>
                      </div>
                      <div className="text-center hidden lg:block" title="Visits">
                        <div className="flex items-center gap-1"><Calendar className="w-3 h-3 text-slate-400" /><span className="text-xs font-bold text-slate-800">{kpis.total_visits || 0}</span></div>
                        <p className="text-[9px] text-slate-400 uppercase">Visits</p>
                      </div>
                      <div className="text-center hidden lg:block" title="Conversion">
                        <div className="flex items-center gap-1"><Users className="w-3 h-3 text-slate-400" /><span className="text-xs font-bold text-slate-800">{kpis.conversion_rate || 0}%</span></div>
                        <p className="text-[9px] text-slate-400 uppercase">Conv</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {grow >= 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> : <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
                        <span className={`text-xs font-bold ${grow >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{grow}%</span>
                      </div>
                      {hasKids && <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{node.children.length}</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
        {tree.length === 0 && (
          <Card className="border border-border/60">
            <CardContent className="p-8 text-center text-slate-400">
              No team hierarchy found.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
